/**
 * The Notifications page's state machine.
 *
 * A hook rather than state inside `index.tsx`, for one reason: root jest
 * compiles no `.tsx`, so logic that lives in a component cannot be executed by
 * a test at all. `react-test-renderer` CAN drive this hook (the agent page does
 * the same thing with `useAgentTurn`), so the four reads, the confirm step and
 * the POST body are all proved by running them.
 *
 * FOUR INDEPENDENT READS, NOT ONE. Each endpoint answers a different question
 * and any of them can fail on its own — a missing permission, a slow query, a
 * deploy in flight. One combined `Promise.all` with a single catch would blank
 * a page that could still answer three quarters of it, so every read wraps its
 * own outcome in a Tile and the page shows the amber "unavailable" state for
 * just that card.
 *
 * TWO GROUPS, BECAUSE ONLY ONE OF THEM DEPENDS ON THE WINDOW. The overview is
 * the only endpoint that takes `days`; the outbox, the controls and the holdout
 * answer about NOW and ignore it. Re-reading all four when the operator moves
 * the 7/30/90 selector would fire three requests whose answers cannot have
 * changed — so the window drives the overview alone, and the other three are
 * re-read on Refresh and after a control write, which are the moments their
 * answers actually can change.
 */
import { useCallback, useEffect, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import {
  DEFAULT_WINDOW,
  fetchControls,
  fetchHoldout,
  fetchOutbox,
  fetchOverview,
  loadingTile,
  submitStageEmailControl,
  validateControlSubmission,
  type ControlAction,
  type ControlsData,
  type PendingControlView,
  type HoldoutData,
  type OutboxData,
  type OverviewData,
  type OverviewWindowDays,
  type Tile,
} from './data';

/** Re-exported so the page and its dialog name ONE shape, declared beside the
 *  other response types rather than twice. */
export type PendingControl = PendingControlView;

export interface NotificationsController {
  days: OverviewWindowDays;
  setDays: (days: OverviewWindowDays) => void;
  overview: Tile<OverviewData>;
  outbox: Tile<OutboxData>;
  controls: Tile<ControlsData>;
  holdout: Tile<HoldoutData>;
  /** True while the FIRST overview read is in flight (the window's own group). */
  overviewLoading: boolean;
  /** True while the FIRST outbox/controls/holdout read is in flight. */
  restLoading: boolean;
  /** Either of the two — for anything that speaks about the page as a whole. */
  loading: boolean;
  refreshing: boolean;
  refresh: () => void;
  /** True only for an admin holding `notifications.controls.write`. */
  canWriteControls: boolean;
  pending: PendingControl | null;
  reason: string;
  setReason: (reason: string) => void;
  submitting: boolean;
  /** The reason a submission was refused, shown inside the dialog. */
  actionError: string | null;
  openControl: (stage: string, action: ControlAction) => void;
  closeControl: () => void;
  confirmControl: () => Promise<void>;
}

export const CONTROLS_WRITE_PERMISSION = 'notifications.controls.write';
// The page's READ permission, `notifications.read`, is not a constant here.
// It had one, exported and imported by nothing, while the live string stayed
// written out in the two places that actually enforce it — the route guard in
// App.tsx and the sidebar entry. A constant no caller reads is not a single
// source of truth; it is a second spelling that can drift from the first
// without anything failing.

export function useNotificationsPage(): NotificationsController {
  const apiUrl = useApiUrl();
  const { hasPermission } = useAdminPermissions();

  const [days, setDaysState] = useState<OverviewWindowDays>(DEFAULT_WINDOW);
  const [overview, setOverview] = useState<Tile<OverviewData>>(loadingTile<OverviewData>());
  const [outbox, setOutbox] = useState<Tile<OutboxData>>(loadingTile<OutboxData>());
  const [controls, setControls] = useState<Tile<ControlsData>>(loadingTile<ControlsData>());
  const [holdout, setHoldout] = useState<Tile<HoldoutData>>(loadingTile<HoldoutData>());
  // Two flags, because the two groups load independently. A card is told about
  // the load that feeds IT, so moving the window never makes the outbox card
  // claim to be reading something.
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [restLoading, setRestLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [pending, setPending] = useState<PendingControl | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  /** The ONLY read that takes the window. */
  const loadOverview = useCallback(
    async (windowDays: OverviewWindowDays) => {
      setOverview(await fetchOverview(apiUrl, windowDays));
      setOverviewLoading(false);
    },
    [apiUrl],
  );

  /** The three reads that answer about NOW and ignore the window. */
  const loadRest = useCallback(async () => {
    const [ob, c, h] = await Promise.all([
      fetchOutbox(apiUrl),
      fetchControls(apiUrl),
      fetchHoldout(apiUrl),
    ]);
    setOutbox(ob);
    setControls(c);
    setHoldout(h);
    setRestLoading(false);
  }, [apiUrl]);

  // Keyed on `days`, so moving the selector re-reads THIS and nothing else.
  useEffect(() => {
    void loadOverview(days);
  }, [loadOverview, days]);

  // Deliberately NOT keyed on `days`: these three would answer identically.
  useEffect(() => {
    void loadRest();
  }, [loadRest]);

  const setDays = useCallback((next: OverviewWindowDays) => setDaysState(next), []);

  const refresh = useCallback(() => {
    setRefreshing(true);
    void Promise.all([loadOverview(days), loadRest()]).finally(() => setRefreshing(false));
  }, [loadOverview, loadRest, days]);

  const openControl = useCallback((stage: string, action: ControlAction) => {
    setPending({ stage, action });
    setReason('');
    setActionError(null);
  }, []);

  const closeControl = useCallback(() => {
    setPending(null);
    setReason('');
    setActionError(null);
  }, []);

  /**
   * The POST, behind the confirm step.
   *
   * The reason is validated HERE and not only by a disabled button: a disabled
   * button is a hint, and this is the last place before a row is written to a
   * table another operator will read.
   */
  const confirmControl = useCallback(async () => {
    if (!pending || submitting) return;
    const refusal = validateControlSubmission(pending.action, reason);
    if (refusal) {
      setActionError(refusal);
      return;
    }
    setSubmitting(true);
    setActionError(null);
    try {
      await submitStageEmailControl(apiUrl, pending.stage, pending.action, reason);
      setPending(null);
      setReason('');
      // Re-read from the API so the page shows what the TABLE now holds, never
      // what this browser believes it wrote. The overview is left alone: a
      // control change cannot alter counts already in the window.
      await loadRest();
    } catch (e) {
      setActionError((e as Error)?.message || 'The change was not saved.');
    } finally {
      setSubmitting(false);
    }
  }, [apiUrl, pending, reason, submitting, loadRest]);

  return {
    days,
    setDays,
    overview,
    outbox,
    controls,
    holdout,
    overviewLoading,
    restLoading,
    loading: overviewLoading || restLoading,
    refreshing,
    refresh,
    canWriteControls: hasPermission(CONTROLS_WRITE_PERMISSION),
    pending,
    reason,
    setReason,
    submitting,
    actionError,
    openControl,
    closeControl,
    confirmControl,
  };
}
